import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { ParagraphComp } from '../../Components/ParagraphComp/ParagraphComp';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getNpoProfileSummary, getUrlList } from '../../Store/npoProfile/npoProfile';
import Summary from './Components/Summary';
import ProgramsAndResults from './Components/ProgramsAndResults';
import Governance from './Components/Governance';
import Operations from './Components/Operations';
import Documents from './Components/Documents';
import Financial from './Components/Financial';
import FooterComp from '../../Components/FooterComp/FooterComp';
import HeaderSec from '../../Components/HeaderSec/HeaderSec';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import ButtonComp from '../../Components/ButtonComp/ButtonComp';
import Loading from '../../Components/Loader/Loading';
import { useScrollToHash } from '../../Hooks/useScrollToHash';

function NpoProfile() {
  const navigate = useNavigate();
  const { orgName } = useParams();
  const [gsi_number, setGsi_number] = useState(orgName)
  const token = localStorage.getItem('NpoToken')

  const [isDownloading, setIsDownloading] = useState(false); // State for loader

  const { data: npoProfileSummary, isLoading: npoProfileSummaryLoading } = useQuery({
    queryKey: ['npoProfileSummary', gsi_number],
    queryFn: () => getNpoProfileSummary(gsi_number),
    refetchOnWindowFocus: false,
    enabled: !!gsi_number,
  });
  const { data: npoCusUrls } = useQuery({
    queryKey: ['getUrlList'],
    queryFn: () => getUrlList(),
    refetchOnWindowFocus: false,
  });
  const location = useLocation();
  useEffect(() => {
    if (!orgName) {
      const currentPath = location.pathname;
      npoCusUrls?.documents.map(urls => {
        let pathname = urls.custom_url;

        try {
          const urlObj = new URL(urls.custom_url);
          pathname = urlObj.pathname; // Extract only the relative path
        } catch (error) {
          // If it's already a relative path, ensure it starts with "/"
          pathname = urls.custom_url?.startsWith('/') ? urls.custom_url : `/${urls.custom_url}`;
        }

        if (pathname === currentPath) {
          setGsi_number(urls.gsi_number)
        }
      })
    }
  }, [npoCusUrls])


  useScrollToHash();


  const profileSummaryData = npoProfileSummary?.documents?.[0] || '';

  const handleDownloadPDF = async () => {
    setIsDownloading(true); // Start loader
    try {
      const element = document.getElementById('pdf-content');
      if (!element) {
        alert('Element not found!');
        setIsDownloading(false); // Stop loader
        return;
      }

      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const imgHeight = (canvasHeight * pdfWidth) / canvasWidth;

      let position = 0;
      let pageHeightLeft = imgHeight;

      while (pageHeightLeft > 0) {
        const sourceY = position * (canvasHeight / imgHeight);
        const canvasPage = document.createElement('canvas');
        canvasPage.width = canvas.width;
        canvasPage.height = canvas.height / (imgHeight / pdfHeight);

        const ctx = canvasPage.getContext('2d');
        ctx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          canvas.height / (imgHeight / pdfHeight),
          0,
          0,
          canvasPage.width,
          canvasPage.height
        );

        const pageData = canvasPage.toDataURL('image/png');
        pdf.addImage(pageData, 'PNG', 0, 0, pdfWidth, pdfHeight);

        position += pdfHeight;
        pageHeightLeft -= pdfHeight;

        if (pageHeightLeft > 0) {
          pdf.addPage();
        }
      }

      pdf.save(`${profileSummaryData?.org_name}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsDownloading(false); // Stop loader
    }
  };

  const PrintPage = () => {
    window.print();
  };

  return (
    <div className='max-w-screen overflow-hidden'>
      <HeaderSec />
      <Box id="pdf-content" sx={{ position: 'relative' }}>
        <Box sx={{ position: '', top: 0, left: 0, right: 0 }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{
              pl: 2,
              height: '72px',
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              borderBottom: '3px solid #0C0C0D0D',
              cursor: 'pointer',
            }}
          >
            <ArrowBackIcon className="text-[#004878]" />
            <ParagraphComp text="Back to Advanced Search" className="text-[#004878] text-xl" />
          </Box>
        </Box>
        {npoProfileSummaryLoading ? (
          <Box sx={{ mt: 5, px: 4, display: 'flex', justifyContent: 'center' }}>
            <Loading />
          </Box>
        ) : (
          <Box sx={{ mt: 2, px: 4 }}>
            {profileSummaryData ?

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box>
                  <ParagraphComp
                    text={`Guidestar Number: ${profileSummaryData?.guidestar_no || '-'}`}
                    className="text-[#004878] text-2xl font-bold"
                  />
                  <Typography
                    variant="h4"
                    fontSize={34}
                    fontWeight={800}
                    sx={{ color: 'black', pt: 0, mt: 1 }}
                  >
                    {profileSummaryData?.org_name}
                  </Typography>
                </Box>
              </Box>
              : ''}
            < Summary id='Summary' summaryData={profileSummaryData} />
            <ProgramsAndResults id='ProgramsAndResults' orgName={gsi_number} />
            <Governance id='Governance' orgName={gsi_number} />
            <Financial id='Financial' orgName={gsi_number} />
            <Operations id='Operations' orgName={gsi_number} />
            <Documents id='Documents' orgName={gsi_number} />
          </Box>
        )}
      </Box>
      {token ?
        <div className="w-[100%] p-[20px] flex gap-[20px] justify-center">
          <ButtonComp
            className="rounded-full px-[20px] border-none bg-[#004878] text-[white]"
            onClick={handleDownloadPDF}
            disabled={isDownloading} // Disable button while downloading
          >
            {isDownloading ? 'Generating PDF...' : 'Download NPO Profile as PDF'}
          </ButtonComp>
          <ButtonComp
            className="rounded-full px-[20px] border-[#004878] border-2 bg-transparent text-[#004878]"
            onClick={PrintPage}
          >
            Print the NPO Profile Page
          </ButtonComp>
        </div>
        : ''
      }
      <FooterComp />
    </div>
  );
}

export default NpoProfile;
